import React, { useEffect, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FormModal } from '@/components/ui/form-modal';
import { TiptapRichTextEditor } from 'components/DesignSystems/New/TiptapRichTextEditor';
import type { SitrepSectionConflictDetails } from 'components/DisasterGPT/hooks/useSitrepsV2';

type SitrepSectionConflictModalProps = {
  show: boolean;
  conflict: SitrepSectionConflictDetails | null;
  draftContent: string;
  onKeepExisting: () => void;
  onOverride: (latestVersionId: string, editedContent: string) => void;
  isOverriding?: boolean;
};

const formatTimestamp = (isoTimestamp: string): string => {
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) return isoTimestamp;
  return date.toLocaleString();
};

const SitrepSectionConflictModal: React.FC<SitrepSectionConflictModalProps> = ({
  show,
  conflict,
  draftContent,
  onKeepExisting,
  onOverride,
  isOverriding = false,
}) => {
  const [editedContent, setEditedContent] = useState(draftContent);

  useEffect(() => {
    if (show) {
      setEditedContent(draftContent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, conflict?.latestVersionId]);

  if (!conflict) return null;

  const otherUserName = conflict.latestUpdatedByUserName ?? 'another user';
  const updatedAt = formatTimestamp(conflict.latestVersionTimestamp);
  const latestText = conflict.latestVersionText ?? '';

  return (
    <FormModal
      open={show}
      onOpenChange={(open) => {
        if (!open) onKeepExisting();
      }}
      title="This section was edited by another user"
      description={
        <>
          {otherUserName} saved changes
          {conflict.sectionName ? ` to ${conflict.sectionName}` : ''} since you
          started editing. Review both versions below and choose whether to
          override with your edits or keep their content.
        </>
      }
      widthClassName="tw:!max-w-5xl tw:sm:!max-w-5xl"
    >
      <div className="tw:grid tw:gap-4 tw:md:grid-cols-2">
        <div className="tw:flex tw:flex-col tw:gap-2">
          <div className="tw:flex tw:items-center tw:gap-2">
            <Badge variant="destructive">Current</Badge>
            <span className="tw:text-sm tw:text-muted-foreground">
              from {otherUserName} at {updatedAt}
            </span>
          </div>
          <div className="tw:min-h-[150px] tw:flex-1 tw:rounded-md tw:border tw:border-border tw:p-2">
            {latestText ? (
              <TiptapRichTextEditor
                value={latestText}
                readOnly
                showToolbar={false}
                normalizeMarkdown
              />
            ) : (
              <em className="tw:text-muted-foreground">No content.</em>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            disabled={isOverriding}
            onClick={onKeepExisting}
          >
            Keep current content
          </Button>
        </div>
        <div className="tw:flex tw:flex-col tw:gap-2">
          <div className="tw:flex tw:items-center tw:gap-2">
            <Badge variant="green">Your edits</Badge>
            <span className="tw:text-sm tw:text-muted-foreground">
              You can still edit before overriding
            </span>
          </div>
          <div className="tw:min-h-[150px] tw:flex-1 tw:rounded-md tw:border tw:border-border tw:p-2">
            <TiptapRichTextEditor
              value={editedContent}
              onChange={(html) => setEditedContent(html)}
              placeholder="Enter section content..."
              disabled={isOverriding}
              minHeight="150px"
              className="w-100"
              normalizeMarkdown
            />
          </div>
          <Button
            type="button"
            disabled={isOverriding}
            onClick={() => onOverride(conflict.latestVersionId, editedContent)}
          >
            {isOverriding ? 'Overriding…' : 'Override with my edits'}
          </Button>
        </div>
      </div>
    </FormModal>
  );
};

export default SitrepSectionConflictModal;
